/**
 * Realtime audit findings for the audit panel (ADR-8): client runs `@farmer/audit-rules`
 * locally for instant feedback while the enumerator types. The server re-runs the exact
 * same package on submit and is the only source of truth — see submissions.submit() /
 * the 422 AUDIT_BLOCKED handling in views/submission-form.js. This module NEVER decides
 * whether a submission is allowed to go through; it only feeds the non-authoritative
 * panel.
 *
 * `runAudit` takes a `choices` argument shaped `{ [formKey]: { [listName]: rows[] } }`
 * (packages/audit-rules/src/eval.ts) — different nesting than the flat listName-keyed
 * index engine/renderer.js builds from a single form's schema, so it's wrapped here too
 * (same adaptation as engine/relevance.js).
 *
 * "Không phải nguồn sự thật" KHÔNG có nghĩa là được chạy trên cấu hình khác. Client và
 * server phải cùng package + cùng config, chỉ khác ở chỗ ai quyết định — nên caller
 * truyền `auditConfig` = `auditRulesSnapshot` của phiên bản phiếu. Từ 2026-08-08 ngưỡng
 * sửa được ở giao diện, nên dùng DEFAULT_AUDIT_CONFIG ở đây không còn là "xấp xỉ đủ
 * tốt" mà là hiện sai hẳn bộ số (bug 2026-09-26: B1 báo lỗi theo ngưỡng 70–120 mặc định
 * trong khi phiên bản đã đặt 1–30).
 */
import { runAudit, checkCompleteness, catalogFor, DEFAULT_AUDIT_CONFIG } from '@farmer/audit-rules';
import * as api from '../api-client.js';

/**
 * @param {string} formKey - một khoá của FORM_KEYS (@farmer/audit-rules)
 * @param {Record<string,string>} answers
 * @param {any[]} choicesSchema - schema.choices from the submission's formSchemaSnapshot
 * @param {object} [opts]
 * @param {string[]} [opts.disabledRules] - mã luật admin đã tắt (`GET /audit-rules`).
 *   Bắt buộc truyền nếu muốn bảng cảnh báo khớp với kết quả server ghi lúc nộp —
 *   server luôn trộn danh sách này vào config trước khi chạy.
 * @param {Record<string,{title?:string,fix?:string}>} [opts.ruleText] - text tiêu đề /
 *   "Nên làm" admin đặt riêng (`GET /audit-rules`). Truyền cùng lý do với
 *   `disabledRules`: server luôn trộn vào config, không truyền thì bảng realtime hiện
 *   text gốc rồi đổi chữ ngay sau khi nộp.
 * @param {any} [opts.auditConfig] - `auditRulesSnapshot` của phiên bản phiếu đang mở
 *   (API trả kèm `GET /submissions/:id`). **Phải truyền.** Bỏ trống thì hàm rơi về
 *   DEFAULT_AUDIT_CONFIG — ngưỡng biên dịch sẵn trong package, KHÔNG phải ngưỡng admin
 *   đặt ở tab "Ngưỡng đối chiếu" — trong khi server luôn chạy bằng snapshot. Hai bên
 *   lệch nhau thì điều tra viên thấy cảnh báo theo ngưỡng cũ rồi nó biến mất sau khi
 *   nộp, hoặc ngược lại. Fallback chỉ còn để phiếu trong hàng đợi offline của bản app
 *   cũ (chưa có trường này) vẫn hiện được bảng cảnh báo.
 */
export function runRealtimeAudit(formKey, answers, choicesSchema, opts = {}) {
  // Loại phiếu chưa có bộ luật nông học (phiếu RCP): runAudit trả [] — dừng sớm, khỏi báo
  // "snapshot thiếu auditRec" cho một phiên bản vốn không cần ngưỡng nào.
  if (!catalogFor(formKey).length) return [];
  const choices = { [formKey]: indexChoices(choicesSchema) };
  // Snapshot thiếu `auditRec` thì mọi luật sẽ ném ở dòng `rec.<khoá>` đầu tiên, và
  // catch bên dưới biến nó thành bảng cảnh báo TRỐNG — trông y hệt "phiếu sạch".
  // Rơi về mặc định và nói to còn hơn im lặng báo an toàn.
  let base = opts.auditConfig;
  if (base && !base.auditRec) {
    console.error('[audit-client] auditRulesSnapshot thiếu `auditRec` — tạm dùng ngưỡng mặc định, bảng cảnh báo sẽ KHÔNG khớp kết quả server', base);
    base = undefined;
  }
  const config = {
    ...(base ?? DEFAULT_AUDIT_CONFIG),
    disabledRules: opts.disabledRules ?? [],
    ruleText: opts.ruleText ?? {},
  };
  try {
    return runAudit(formKey, answers, config, choices);
  } catch (err) {
    console.error('[audit-client] runAudit threw — realtime panel will show stale/empty results until fixed', err);
    return [];
  }
}

/**
 * Câu bắt buộc còn trống + ô vi phạm `constraint`, đọc từ chính `formSchemaSnapshot`
 * của phiếu. Cùng hàm mà server chạy lúc nộp (`AuditService.checkCompleteness`), nên
 * danh sách hiện trong bảng cảnh báo đúng bằng danh sách sẽ chặn phiếu — điều tra viên
 * không còn bấm "Nộp" rồi mới biết còn thiếu 12 ô.
 *
 * Vẫn không phải nguồn sự thật (ADR-8): server chạy lại trên `answers` đã lưu, nên một
 * ô vừa gõ mà chưa autosave xong có thể lệch trong khoảnh khắc — submit() luôn
 * `await doAutosave()` trước khi gọi API chính vì lý do đó.
 *
 * @param {string} formKey - một khoá của FORM_KEYS (@farmer/audit-rules)
 * @param {{survey: any[], choices: any[]}} schema - submission.formSchemaSnapshot
 * @param {Record<string,string>} answers - đã trộn lớp chiếu master data
 * @param {{dynamicLists?: Record<string, any[]>}} [opts] - danh sách động API trả kèm phiếu
 */
export function runRealtimeCompleteness(formKey, schema, answers, opts = {}) {
  try {
    return checkCompleteness(schema, answers, formKey, opts);
  } catch (err) {
    console.error('[audit-client] checkCompleteness threw — panel sẽ thiếu phần kiểm tra bắt buộc', err);
    return [];
  }
}

function indexChoices(list) {
  const map = {};
  (list || []).forEach((c) => {
    const k = c['list name'];
    if (k) (map[k] = map[k] || []).push(c);
  });
  return map;
}

/**
 * Server-confirmed findings for a submission that's already been through at least one
 * submit/audit-preview cycle — DB rows, NOT the same shape as Finding[] (nowText/recText/
 * fixText/relatedKeys instead of now/rec/fix/keys). Normalize before handing to
 * ui/findings-panel.js.
 */
export function normalizeStoredFinding(row) {
  return {
    level: row.level,
    code: row.code,
    title: row.title,
    now: row.nowText ?? row.now ?? '',
    rec: row.recText ?? row.rec ?? '',
    fix: row.fixText ?? row.fix ?? '',
    keys: row.relatedKeys ?? row.keys ?? [],
    overridden: row.overridden,
    overrideReason: row.overrideReason,
  };
}

export async function fetchAuditPreview(submissionId) {
  const res = await api.submissions.auditPreview(submissionId);
  return res?.auditFindings || [];
}
